import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { DropDownListComponent, MultiSelectComponent, VirtualScroll, DropDownTreeComponent } from '@syncfusion/ej2-angular-dropdowns';
import { AppSettings } from './shared/Constants/sharedConstants';
import { Subject, takeUntil } from 'rxjs';
import { ThemeSettingsService } from '@services/theme-settings.service';
import { filter } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';
import { CurrentTheme } from '@models/uiapp-config';
import { UtilsService } from '@services/utils.service';
import { LogService } from '@services/log.service';
import { isTrustedScriptUrl } from '@utils/safe-url';

MultiSelectComponent.Inject(VirtualScroll);
DropDownListComponent.Inject(VirtualScroll);
DropDownTreeComponent.Inject(VirtualScroll);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy, OnInit {

  private _destroy$ = new Subject<boolean>();
  constructor(private utilsService: UtilsService, private logger: LogService ) {
    this.initializeWalkMe();
    this.initializeAnalytics();
    //this.initializeNewAnalytics();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  ngOnInit(): void {
    // Remove the logo container after a short delay to ensure the fade-out is complete
    setTimeout(() => {
      const logoContainer = document.querySelector('.load_center');
      if (logoContainer) {
        logoContainer.remove();
      }
    }, 3000); // 500ms should be enough time for the fade-out to complete
  }

  /**
   * Both integrations below used to assemble JavaScript source in a template literal and hand
   * it to `script.innerHTML`, so the configured values were parsed as code: a single quote in
   * a URL closed the string it sat in, and the Matomo site id was interpolated unquoted into
   * expression position, where no quote was needed at all.
   *
   * Each vendor snippet only ever created a script element and set one global, so both are
   * written against the DOM API here. Nothing turns text into code any more, which is what
   * removes the flaw - the scheme checks are defence in depth on top of that.
   */
  private initializeWalkMe(): void {
    const src = AppSettings?.UIAppConfig?.walkMeSrc;
    if (!src || src === 'NOT SET') {
      return;
    }

    if (!isTrustedScriptUrl(src)) {
      this.logger.error('WalkMe source is not a trusted script URL; skipping WalkMe.', src);
      return;
    }

    const walkme = document.createElement('script');
    walkme.type = 'text/javascript';
    walkme.async = true;
    walkme.src = src;
    document.head.appendChild(walkme);

    window['_walkmeConfig'] = { smartLoad: true };
  }

  private initializeAnalytics(): void {
    const trackerUrl = AppSettings?.UIAppConfig?.siteTrackerUrl;
    const siteId = Number(AppSettings?.UIAppConfig?.siteTrackerSiteId);

    if (!trackerUrl || !(siteId > 0)) {
      return;
    }

    if (!isTrustedScriptUrl(trackerUrl)) {
      this.logger.error('Site tracker URL is not a trusted script URL; skipping analytics.', trackerUrl);
      return;
    }

    // Matomo treats this as a directory URL and appends the file name to it, so whatever
    // trailing slash the configured value carries is preserved exactly as before.
    const paq = window['_paq'] = window['_paq'] || [];
    paq.push(['trackPageView']);
    paq.push(['enableLinkTracking']);
    paq.push(['setTrackerUrl', `${trackerUrl}matomo.php`]);
    paq.push(['setSiteId', siteId]);

    const matomo = document.createElement('script');
    matomo.type = 'text/javascript';
    matomo.async = true;
    matomo.src = `${trackerUrl}matomo.js`;
    document.head.appendChild(matomo);
  }
}
